import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import Cookies from 'js-cookie';

import { CurrentCommunityType } from 'types';
import { TELEGRAM_SUBDOMAIN_SUFFIX } from 'components/telegram';
import {
  GENERAL_SUBDOMAIN,
  generalHostFromWindow,
  COMMUNITY_SWITCHER_COOKIE,
  GeneralCookieHost
} from 'app/_auth/subdomains';

type CommunityContextType = {
  community: CurrentCommunityType | null;
  chooseCommunity: (community?: CurrentCommunityType) => void;
};

export const CommunityContext = createContext({
  community: null,
  chooseCommunity: () => {},
} as CommunityContextType);


const STOP_SUBDOMAINS = ['www'];

export const CommunityContextProvider = ({ children }: { children: ReactNode }) => {
  const params = useSearchParams();
  const [chosenCommunity, setChoseCommunity] = useState<CurrentCommunityType | null>(null);

  const community = useMemo(() => {
    // 1. READ FROM STATE
    if (chosenCommunity !== null) return chosenCommunity;
    if (typeof window == 'undefined') return null;

    // 2. READ FROM COOKIE
    const cookieCommunity = Cookies.get(COMMUNITY_SWITCHER_COOKIE);
    if (cookieCommunity && cookieCommunity !== '') {
      return {
        name: cookieCommunity,
        subdomain: cookieCommunity
      } as CurrentCommunityType;
    }

    // 3. READ FROM SUBDOMAIN
    let subdomain = window.location.hostname.split('.')[0];
    if (STOP_SUBDOMAINS.includes(subdomain)) return null;

    if (subdomain && subdomain.endsWith(TELEGRAM_SUBDOMAIN_SUFFIX)) {
      subdomain = subdomain.replace(TELEGRAM_SUBDOMAIN_SUFFIX, '');
    }

    if (subdomain === GENERAL_SUBDOMAIN) {
      subdomain = params?.get('community') || '';
    }
    return {
      name: subdomain,
      subdomain: subdomain
    } as CurrentCommunityType;
  }, [chosenCommunity, params]);

  const chooseCommunity = (community?: CurrentCommunityType) => {
    const domain = GeneralCookieHost(generalHostFromWindow(window));
    setChoseCommunity(community || null);
    const cookieDomain = domain ? `domain=${domain};` : '';
    document.cookie = `${COMMUNITY_SWITCHER_COOKIE}=${community?.subdomain || ''}; path=/; ${cookieDomain}`;
  };

  return (
    <CommunityContext.Provider value={{
      community: community,
      chooseCommunity: chooseCommunity
    }}>
      {children}
    </CommunityContext.Provider>
  );
};

export function useCommunity() {
  return useContext(CommunityContext);
}
